import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Box, Divider, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import { Alert, Button, Input, Skeleton } from '@repo/ui'
import {
  AddressFieldGroup,
  EMPTY_ADDRESS_FIELD_GROUP_VALUE,
  hasAddressFieldGroupErrors,
  validateAddressFieldGroup,
  type AddressFieldGroupValue,
} from 'components'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'

import { STRIPE_PUBLIC_KEY } from 'config'
import { signupApi } from 'services/signupApi'
import { resolvePlanDescription } from 'constants/planComparison'
import { centsToDollar } from 'utils'

import { signupPaymentAppearance } from './signupPaymentAppearance'

/**
 * VERA-1095 — the signup stepper's PAYMENT step: Stripe **Elements**, styled to
 * match the three steps before it.
 *
 * WHAT THIS REPLACED, AND WHY IT HAD TO BE REPLACED RATHER THAN RESTYLED.
 * This step used to be `EmbeddedCheckoutProvider` / `EmbeddedCheckout`
 * (`ui_mode: 'embedded'`), which is a Stripe-OWNED iframe. The client-side
 * `appearance` API does not cross into it, and the only lever a session has is
 * `branding_settings` — which, measured against the live API on 2026-08-25 with
 * a `background_color: #ff0000` probe, colours the ORDER SUMMARY pane and has
 * no parameter that reaches the payment-form pane. The result on a dark signup
 * page was a dark summary blending into the page above a white form card. That
 * is a hard ceiling of the integration, not a shortcut anyone took, so the fix
 * was to change integration.
 *
 * `Elements` + `PaymentElement` is the one that supports `appearance` on the
 * pinned `@stripe/stripe-js` (1.54.2 — verified: `PaymentElement` and
 * `appearance` are both in its types). `ui_mode: 'custom'` would also work and
 * is NOT used: it needs `initCheckout`, which arrived in a much later stripe-js,
 * and upgrading v1 to v7 would touch every Stripe surface in the app (billing,
 * payment methods) for one step's styling.
 *
 * THREE THINGS THIS COMPONENT OWNS THAT THE IFRAME USED TO:
 *
 *  1. THE SUMMARY HEADER. Written from the plan the visitor already picked,
 *     which is on the client already (`signupApi.listPlans`). Deliberately NOT
 *     read back from Stripe: Checkout's own summary showed Stripe's product
 *     name and price, which is billing copy maintained in a different system
 *     (it has already been renamed once — "Vera Enterprise" -> "Vera Scale",
 *     VRG-393) and read as a second, competing description of a choice the
 *     visitor had just made two steps earlier.
 *  2. THE SUBMIT BUTTON. Ours (`AuthPrimaryButton`, in the page's footer), not
 *     Stripe's purple one. See `registerSubmit` for the wiring.
 *  3. ERRORS. Card errors that Stripe used to show inside its own frame now
 *     come back from `confirmPayment` and are raised to the page's existing
 *     submit-error Alert, so a declined card and a failed network call appear
 *     in the same place.
 *
 * WHY THE CLIENT SECRET IS STILL NOT FETCHED HERE (unchanged from the embedded
 * version, and still the load-bearing reason this component takes it as a prop):
 * the call that mints it is ALSO the call that creates the founder's WorkOS
 * user, reserves their subdomain and writes the pending-signup row, so it can
 * fail with "that subdomain is taken" or "an account already exists for this
 * email" — answers to fields on EARLIER steps. Those must surface on the plan
 * step, beside the CTA that triggered them and while the visitor can still go
 * back and fix the field at fault, not as a dead form on a step they were
 * advanced to by a call that did not actually succeed.
 */

// Module-level, matching the other Stripe call sites in this app
// (TagsShopCheckoutPage, CreatePaymentMethodModal): `loadStripe` injects a
// script tag and must not re-run per render.
const stripePromise = STRIPE_PUBLIC_KEY ? loadStripe(STRIPE_PUBLIC_KEY as string) : null

/**
 * Lato, delivered INTO the Stripe iframe.
 *
 * `appearance.variables.fontFamily` only names a family — the frame is a
 * separate document and cannot see the app's own `@import` (index.css). Without
 * this, Stripe falls back to its own sans, and a form in the wrong typeface
 * reads as foreign no matter how exactly its borders match. Same URL and same
 * weights as index.css, so the frame and the page load one font, once.
 */
const ELEMENTS_FONTS = [
  {
    cssSrc: 'https://fonts.googleapis.com/css2?family=Lato:wght@300;400;500;600;700&display=swap',
  },
]

export interface SignupPaymentPlan {
  title: string
  /** Cents — the shape `GET /signup/plans` returns. */
  amount: number
  /** 'month' | 'year' | 'annual' … — normalised for display below. */
  interval: string
  description?: string | null
  /**
   * The tier's stable identity, used to resolve the REPO-owned description
   * (VERA-1095). Structurally already present on the `PlanColumnPlan` the plan
   * step selects and hands straight to this component, so naming it here costs
   * the call site nothing.
   *
   * Without it this summary would render the raw Stripe-mirrored
   * `plan.description` while the matrix one click earlier rendered the repo's —
   * i.e. the same field saying two different things two steps apart, which is
   * exactly the drift `resolvePlanDescription` exists to remove.
   */
  tier_key?: string | null
}

export interface SignupPaymentStepProps {
  /**
   * The PaymentIntent's client secret, or null while one has not been minted.
   * Null renders the skeleton: the step is only ever reached WITH a secret, and
   * the plan step's own CTA carries the loading state until then.
   *
   * Changing it remounts `Elements` (it is the component `key`), because a
   * secret is bound to the plan and interval it was minted with — going back to
   * change plan mints a genuinely different PaymentIntent.
   */
  clientSecret: string | null
  /** The plan the visitor chose, for OUR summary header. */
  plan: SignupPaymentPlan | null
  /**
   * The founder's name from STEP 1, prefilled into the billing address's Name
   * field so nobody types their own name twice in one form.
   *
   * A DEFAULT, NOT A LOCK — the field stays editable, because the name on the
   * card is genuinely allowed to differ from the name on the account (a company
   * card, a spouse's card, a legal name that is not the one they introduced
   * themselves by).
   *
   * The caller is expected to have joined first + last already; last name is
   * optional on step 1, so an empty string here means "nothing to prefill" and
   * the field is simply left blank.
   */
  defaultName?: string | null
  /**
   * Hands the page a function that submits this form, so the page's existing
   * footer CTA can drive it.
   *
   * The indirection is unavoidable: `useStripe`/`useElements` only work INSIDE
   * `<Elements>`, and the button lives outside it, in the page's footer beneath
   * the error Alert. The alternative — a second button inside this card — would
   * put the payment step's CTA somewhere no other step's CTA is.
   *
   * Called with `null` on unmount so the page cannot hold a stale closure over
   * an `Elements` instance that is gone.
   */
  registerSubmit: (submit: (() => Promise<void>) | null) => void
  /** Raised to the page's submit-error Alert — the same one every other step's
   * failures appear in. */
  onError: (message: string | null) => void
  /**
   * A DEFINITIVE refusal (a `card_error` from Stripe, or an intent that came
   * back still needing a payment method) as distinct from a transient problem.
   *
   * The split matters because the two deserve different screens: a dropped
   * connection wants the form left exactly as typed with a line of red above
   * it, whereas a declined card wants to say that the card was declined, that
   * no money moved, and offer a genuinely different next step. Everything that
   * is not clearly a refusal goes to `onError`, because over-reporting a
   * refusal is the worse mistake of the two.
   */
  onFailed: (message: string) => void
  /**
   * Set while a definitive failure is showing. Replaces the card form with the
   * failure panel; cleared by the page once a fresh secret has been minted.
   */
  failure?: string | null
  /** Re-mint and return to the form. See `remintPayment` on the page. */
  onRetry?: () => void
  /** True while that re-mint is in flight. */
  retrying?: boolean
  /** Fires once the PaymentIntent is confirmed, with its id, so the page can go
   * verify + provision. */
  onSucceeded: (paymentIntentId: string) => void
  /** Mirrors the in-flight state up so the footer CTA can show its spinner and
   * refuse a second click. */
  onSubmittingChange: (submitting: boolean) => void
}

/** 'month' | 'monthly' | 'year' | 'annual' … -> the noun we print after "/". */
const intervalNoun = (interval: string): string => {
  const value = (interval || '').toLowerCase()
  if (value === 'year' || value === 'annual' || value === 'annually') return 'year'
  return 'month'
}

/** One labelled field, the shape every Stripe row takes. */
const FieldSkeleton = ({ labelWidth }: { labelWidth: number }) => (
  <Box>
    <Skeleton width={labelWidth} height={14} sx={{ mb: 2 }} />
    <Skeleton height={49} />
  </Box>
)

/**
 * §19: the skeleton occupies the layout the two Stripe elements will occupy —
 * the card rows (number, then the half-width expiry/CVC pair), the section
 * heading, then the billing-address rows. Not a spinner, and not a single
 * generic block: the elements take a moment to mount, and a placeholder that is
 * the wrong shape makes the step jump when they arrive.
 *
 * FOOTPRINT PARITY IS THE POINT, so this had to grow when the address fields
 * were added — a skeleton still reserving only the card rows would have left
 * the panel visibly springing open by the height of a whole address form the
 * moment Stripe reported ready.
 *
 * It reserves what OUR billing block renders, one row each: the heading, Full
 * name, Country, Address, Apartment, the City / Postal pair, and State. Note
 * this is now a slight OVER-reservation rather than an under-one, because our
 * fields render immediately while only the card iframe is awaited — the
 * skeleton is visible for the card's mount alone. Over-reserving is the right
 * side to err on: the block settles by shrinking into place rather than
 * shoving the submit button down the page.
 */
const PaymentFieldsSkeleton = () => (
  <Stack spacing={4}>
    <FieldSkeleton labelWidth={96} />
    <Stack direction="row" spacing={3}>
      <Box sx={{ flex: 1 }}>
        <FieldSkeleton labelWidth={72} />
      </Box>
      <Box sx={{ flex: 1 }}>
        <FieldSkeleton labelWidth={48} />
      </Box>
    </Stack>
    <Box sx={{ pt: 2 }}>
      <Skeleton width={112} height={16} />
    </Box>
    <FieldSkeleton labelWidth={64} />
    <FieldSkeleton labelWidth={56} />
    <FieldSkeleton labelWidth={88} />
    <Stack direction="row" spacing={3}>
      <Box sx={{ flex: 1 }}>
        <FieldSkeleton labelWidth={40} />
      </Box>
      <Box sx={{ flex: 1 }}>
        <FieldSkeleton labelWidth={76} />
      </Box>
    </Stack>
  </Stack>
)

/**
 * OUR order summary — plan name and price, from the plan object the visitor
 * selected two steps ago.
 *
 * A plain heading block, not a card. The panel it sits in is already a surface;
 * boxing the summary would put a container inside a container, which is the
 * nesting the embedded version was criticised for.
 */
const PlanSummary = ({ plan }: { plan: SignupPaymentPlan }) => (
  <Box>
    <Typography variant="body3" sx={{ color: 'text.secondary' }}>
      You're subscribing to
    </Typography>
    <Stack
      direction="row"
      alignItems="baseline"
      justifyContent="space-between"
      spacing={3}
      sx={{ mt: 2 }}
    >
      <Typography variant="body1" sx={{ fontWeight: 600 }}>
        {plan.title}
      </Typography>
      <Typography variant="body1" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
        {/* `amount` is cents and plan prices are whole dollars, so no decimals —
            the same call the plan matrix makes. */}
        {centsToDollar(plan.amount)}
        <Typography component="span" variant="body3" sx={{ color: 'text.secondary' }}>
          {` / ${intervalNoun(plan.interval)}`}
        </Typography>
      </Typography>
    </Stack>
    {/* The repo-owned line for this tier, the same one the plan matrix rendered
        on the previous step — not the raw Stripe-mirrored `plan.description`
        (VERA-1095, see `resolvePlanDescription`). */}
    {resolvePlanDescription(plan) && (
      <Typography
        variant="body3"
        sx={{ color: 'text.secondary', mt: 2, lineHeight: 1.5, display: 'block' }}
      >
        {resolvePlanDescription(plan)}
      </Typography>
    )}
    <Divider sx={{ mt: 5, mb: 5 }} />
  </Box>
)

/**
 * THE PAYMENT-FAILURE PANEL.
 *
 * Shown instead of the card form after a definitive refusal. Three things it
 * has to do, in this order, because this is the one screen in the flow where
 * someone is most likely to abandon:
 *
 *  1. SAY WHAT HAPPENED, in Stripe's cardholder-facing words ("Your card was
 *     declined.") rather than ours.
 *  2. SAY NO MONEY MOVED, first and unhedged. It is the question the visitor
 *     actually has, and leaving it to be inferred from the absence of a charge
 *     is how a failed signup becomes a support ticket.
 *  3. SAY THEIR WORK IS SAFE. Everything they typed over three steps is still
 *     held — this panel replaces the CARD FIELDS only; the stepper, the plan
 *     choice and every company detail are untouched behind it. Nobody re-enters
 *     anything.
 *
 * The retry re-mints (see `remintPayment`) rather than re-confirming the dead
 * intent, which is why it is a button here and not simply "press Continue
 * again".
 */
const PaymentFailurePanel = ({
  message,
  onRetry,
  retrying,
}: {
  message: string
  onRetry?: () => void
  retrying?: boolean
}) => (
  <Stack spacing={5}>
    <Alert severity="error" description={message} />
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        You haven't been charged.
      </Typography>
      <Typography
        variant="body3"
        sx={{ color: 'text.secondary', mt: 2, lineHeight: 1.5, display: 'block' }}
      >
        Everything you've entered is saved. Start the payment again to enter a different card, or
        go back to pick a different plan.
      </Typography>
    </Box>
    {onRetry && (
      <Button fullWidth loading={retrying} onClick={onRetry}>
        Start payment again
      </Button>
    )}
  </Stack>
)

/** The half that needs `useStripe`/`useElements`, i.e. must live inside `<Elements>`. */
const PaymentForm = ({
  registerSubmit,
  onError,
  onFailed,
  onSucceeded,
  onSubmittingChange,
  defaultName,
}: Pick<
  SignupPaymentStepProps,
  | 'registerSubmit'
  | 'onError'
  | 'onFailed'
  | 'onSucceeded'
  | 'onSubmittingChange'
  | 'defaultName'
>) => {
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
  const stripe = useStripe()
  const elements = useElements()
  // Only the CARD element is Stripe's now — the billing address below is ours,
  // and renders immediately — so `ready` is back to gating on one thing.
  const [ready, setReady] = useState(false)

  // The billing address, ours. Held here rather than inside the field group so
  // `submit` can read it: it has to travel to Stripe on confirm (see
  // `payment_method_data` below), which is the whole reason this is a
  // controlled form and not an uncontrolled one.
  const [billingName, setBillingName] = useState(defaultName?.trim() ?? '')
  const [address, setAddress] = useState<AddressFieldGroupValue>(
    EMPTY_ADDRESS_FIELD_GROUP_VALUE,
  )
  // Off until the first failed submit, so an untouched form is not already red.
  const [showAddressErrors, setShowAddressErrors] = useState(false)

  /**
   * True while the page's Alert is showing OUR address message — the one raised
   * in `submit` below — and nothing else. It is what lets the effect under it
   * clear that Alert without ever clearing somebody else's.
   *
   * The Alert lives on the page, not here, and until VERA-1095 nothing cleared
   * it except the NEXT submit. So "Please complete your billing address."
   * outlived the condition it described: the visitor filled in the address,
   * watched every inline field error go green, and the banner above the pay
   * button still told them the address was incomplete. Same defect the subdomain
   * error had (`SignUpPage`'s `revalidateIfErrored`, and the `setError(null)` in
   * `goBack`) — a message that does not re-evaluate becomes a liar the moment
   * the thing it names is fixed, and this one sits directly above the button it
   * is discouraging them from pressing.
   *
   * A ref rather than state on purpose: the effect below already re-runs on
   * every `address` change, and this flag must not add a render of its own to
   * each keystroke.
   */
  const addressErrorRaisedRef = useRef(false)

  // Step 1's name arriving (or changing, if they went back and fixed a typo)
  // re-seeds the field — but ONLY while the visitor has not typed their own.
  // Overwriting a name they deliberately changed, because a re-mint re-ran this,
  // would be the form arguing with them.
  const nameTouchedRef = useRef(false)
  useEffect(() => {
    if (nameTouchedRef.current) return
    setBillingName(defaultName?.trim() ?? '')
  }, [defaultName])

  // The country list, from the PUBLIC signup route rather than the picker's own
  // `/common/countries` — that one is behind AuthGuard and a visitor here has no
  // session, so letting the picker fetch would 401 into an empty dropdown.
  // Failure is silent by design: an empty list degrades the picker, and an error
  // banner about countries above a payment form would be alarming out of all
  // proportion to a dropdown that has not loaded yet.
  const [countries, setCountries] = useState<{ code: string; name: string }[]>([])
  useEffect(() => {
    let cancelled = false
    signupApi
      .listCountries()
      .then((list) => {
        if (!cancelled) setCountries(list)
      })
      .catch(() => {
        // Deliberately swallowed — see the note above.
      })
    return () => {
      cancelled = true
    }
  }, [])

  /**
   * Retracts the address message as soon as the address is COMPLETE, rather
   * than at the next submit. Scoped twice over, so it can only ever clear the
   * thing it put there:
   *
   *  - `addressErrorRaisedRef` — an unrelated message (a Stripe
   *    `api_connection_error`, a failed element load) is left alone even though
   *    editing the address re-runs this.
   *  - full validity — a PARTIAL fix keeps the banner, which is right: it says
   *    "complete your billing address", and a half-filled one still isn't.
   */
  useEffect(() => {
    if (!addressErrorRaisedRef.current) return
    if (hasAddressFieldGroupErrors(validateAddressFieldGroup(address))) return
    addressErrorRaisedRef.current = false
    onError(null)
  }, [address, onError])

  const submit = useCallback(async () => {
    if (!stripe || !elements) return
    onError(null)
    addressErrorRaisedRef.current = false

    // OUR fields, so OUR validation. Stripe used to do this for us — an
    // AddressElement in the same Elements group is validated as part of
    // `confirmPayment` — and the moment the address became our own component
    // that stopped being true. Without this the form would post a half-empty
    // address to Stripe and surface whatever Stripe said about it, which is a
    // worse message than naming the empty field.
    if (hasAddressFieldGroupErrors(validateAddressFieldGroup(address))) {
      setShowAddressErrors(true)
      onError('Please complete your billing address.')
      // Claims the Alert, so the effect above may retract it once the address
      // is complete. Cleared again on the next submit, four lines up.
      addressErrorRaisedRef.current = true
      return
    }

    onSubmittingChange(true)
    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        // `if_required` rather than always redirecting. Card is the only method
        // enabled server-side, so 3DS is handled in an overlay and the visitor
        // never leaves the stepper — which is the entire point of moving this
        // step in-page. `return_url` is still REQUIRED by Stripe even so, and is
        // genuinely used if a method that must redirect is ever enabled; it is
        // built from the live origin rather than an env var so it is the origin
        // the visitor is actually on.
        confirmParams: {
          return_url: `${window.location.origin}/signup/success`,
          /**
           * THE ADDRESS TRAVELS TO STRIPE. This is the load-bearing half of
           * replacing Stripe's AddressElement with our own fields.
           *
           * Two things depend on it. Stripe needs an address on the charge for
           * AVS and tax; and — the reason it is not optional — provisioning
           * reads the billing address back off the CONFIRMED PaymentIntent
           * server-side rather than trusting anything the browser posts to
           * `/signup/verify`. Sending it here is what keeps that authoritative
           * read possible now that the field is ours: Stripe stores it against
           * the PaymentMethod, and the server reads Stripe, not us.
           *
           * Required, not merely allowed: `fields.billingDetails` below sets
           * `name` and `address` to 'never', and stripe-js's own type doc for
           * this parameter says fields omitted that way must be supplied here.
           *
           * Empty optional parts are sent as `undefined`, never `''` — an empty
           * string is a value, and would overwrite what Stripe can otherwise
           * infer.
           */
          payment_method_data: {
            billing_details: {
              name: billingName.trim() || undefined,
              address: {
                line1: address.address_line.trim(),
                line2: address.address_line_2.trim() || undefined,
                city: address.city.trim(),
                state: address.state.trim() || undefined,
                postal_code: address.postal_code.trim(),
                country: address.country,
              },
            },
          },
        },
        redirect: 'if_required',
      })

      if (error) {
        // Stripe's own message is the right one to show for a card error — it
        // is written for cardholders ("Your card was declined.") and localised.
        // Only the unexpected-shape case gets our wording.
        const message = error.message ?? 'We could not confirm your payment. Please try again.'
        // `card_error` is Stripe's own name for "the card was refused" —
        // declines, expired cards, insufficient funds, incorrect CVC. Anything
        // else (`api_connection_error`, `rate_limit_error`, a validation error
        // for a half-filled field) is transient or correctable in place, and
        // belongs in the inline Alert above an untouched form.
        if (error.type === 'card_error') onFailed(message)
        else onError(message)
        onSubmittingChange(false)
        return
      }

      if (paymentIntent?.status === 'succeeded' || paymentIntent?.status === 'processing') {
        // `processing` counts as done HERE, not as success: some methods settle
        // asynchronously, and the success page already polls /signup/verify and
        // renders a "payment received, still settling" state for exactly this.
        // Sending them there is right; claiming failure would not be.
        onSucceeded(paymentIntent.id)
        return
      }

      // No error and not succeeded — `requires_payment_method` after a silent
      // decline is the realistic case, which is a refusal, so it takes the
      // failure panel rather than the inline Alert.
      onFailed('That payment did not go through. Your card has not been charged.')
      onSubmittingChange(false)
    } catch (err) {
      onError(
        err instanceof Error && err.message
          ? err.message
          : 'We could not confirm your payment. Please try again.',
      )
      onSubmittingChange(false)
    }
  }, [
    stripe,
    elements,
    onError,
    onFailed,
    onSucceeded,
    onSubmittingChange,
    address,
    billingName,
  ])

  // Registered only once the element is READY and Stripe has loaded. Before
  // that, `confirmPayment` would reject with an un-actionable error, so the page
  // keeps its CTA disabled instead.
  useEffect(() => {
    registerSubmit(ready && stripe && elements ? submit : null)
    return () => registerSubmit(null)
  }, [registerSubmit, submit, ready, stripe, elements])

  return (
    <Box sx={{ position: 'relative' }}>
      {!ready && <PaymentFieldsSkeleton />}
      {/* Mounted but hidden until ready, rather than not mounted: `onReady` only
          fires for an element that is actually in the tree, so gating the mount
          on `ready` would deadlock. Height 0 while hidden so it does not reserve
          space beside the skeleton that is standing in for it. */}
      {/* FADES IN rather than appearing. `ready` flips in one frame when
          Stripe's iframe reports itself mounted, and the fields used to snap
          from height 0 / opacity 0 straight to full — which reads as a flash
          against the skeleton that was standing in for them.

          `grid-template-rows: 0fr -> 1fr` carries the HEIGHT, for the same
          reason it carries the CTA reveal on the success page: a grid row in
          `fr` is animatable where `height: auto` is not, so the container grows
          to exactly the PaymentElement's height without anyone measuring an
          iframe whose height we do not control. The inner box must keep
          `overflow: hidden` and `minHeight: 0` or the row never collapses.

          Opacity is delayed 80ms behind the height so the fields are not
          fading in while the box is still visibly growing under them. */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateRows: ready ? '1fr' : '0fr',
          transition: prefersReducedMotion
            ? 'none'
            : 'grid-template-rows 320ms cubic-bezier(0.22, 1, 0.36, 1)',
        }}
      >
        <Box
          sx={{
            overflow: 'hidden',
            minHeight: 0,
            opacity: ready ? 1 : 0,
            transition: prefersReducedMotion ? 'none' : 'opacity 260ms ease-out 80ms',
          }}
        >
          <PaymentElement
            options={{
              layout: 'tabs',
              /**
               * STRIPE COLLECTS NO BILLING DETAILS. Both are ours.
               *
               * Left at its default ('auto'), PaymentElement asks for country
               * and postal code — which is not an address anyone can post
               * anything to, and is a second, differently-styled country picker
               * sitting above the one below.
               *
               * 'never' here is a CONTRACT, not just a hide: stripe-js requires
               * that anything omitted this way be supplied on confirm via
               * `payment_method_data.billing_details`, which `submit` does. The
               * two must move together — turning a field back to 'auto' without
               * removing it there makes Stripe reject the confirmation.
               */
              fields: { billingDetails: { name: 'never', address: 'never' } },
            }}
            onReady={() => setReady(true)}
            onLoadError={(event) => {
              // Releases the Alert first: this message is not ours to retract,
              // and a load failure must not be cleared by the visitor happening
              // to finish typing their address afterwards.
              addressErrorRaisedRef.current = false
              onError(
                event.error?.message ??
                  "The payment form couldn't load. Please refresh and try again.",
              )
            }}
          />

          <Typography variant="body2" sx={{ fontWeight: 600, mt: 5, mb: 4, display: 'block' }}>
            Billing address
          </Typography>

          <Stack spacing={4}>
            {/* Name first, because it is the one field that arrives prefilled —
                leading with something already answered makes the block read as
                shorter than it is. Editable: the name on the card is allowed to
                differ from the name on the account. */}
            <Input
              label="Full name"
              placeholder="Name on card"
              value={billingName}
              onValueChange={(value) => {
                nameTouchedRef.current = true
                setBillingName(value)
              }}
            />
            <AddressFieldGroup
              value={address}
              onChange={setAddress}
              googleMapsApiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
              countries={countries}
              showErrors={showAddressErrors}
            />
          </Stack>
        </Box>
      </Box>
    </Box>
  )
}

export const SignupPaymentStep = ({
  clientSecret,
  plan,
  registerSubmit,
  onError,
  onFailed,
  onSucceeded,
  onSubmittingChange,
  failure,
  onRetry,
  retrying,
  defaultName,
}: SignupPaymentStepProps) => {
  const theme = useTheme()

  // Recomputed on a theme flip, and `Elements` applies an appearance change in
  // place (it calls `elements.update`) rather than remounting — so toggling
  // light/dark mid-payment restyles the fields without discarding typed card
  // details. That is the one thing the embedded version could not do at all: a
  // session's branding is fixed when Stripe mints it.
  const appearance = useMemo(() => signupPaymentAppearance(theme), [theme])

  if (!stripePromise) {
    // A missing publishable key is a deployment fault, not a user error, and it
    // is worth saying so plainly rather than rendering an empty step that looks
    // like a slow network. Same guard the app's other Stripe surfaces use.
    return (
      <Alert
        severity="error"
        description="Payment isn't configured for this environment. Please contact support."
      />
    )
  }

  return (
    // NO background, NO card, NO nested container. The panel around this step
    // is already the surface; the second, differently-coloured pane the
    // embedded integration painted here is the thing this rebuild removes.
    <Box sx={{ width: '100%' }}>
      {/* The plan summary stays visible through a failure — it is the "here is
          what you were buying" anchor, and hiding it would make the panel read
          as a dead end rather than as a step that can be repeated. */}
      {plan && <PlanSummary plan={plan} />}
      {failure ? (
        <PaymentFailurePanel message={failure} onRetry={onRetry} retrying={retrying} />
      ) : clientSecret ? (
        <Elements
          key={clientSecret}
          stripe={stripePromise}
          options={{ clientSecret, appearance, fonts: ELEMENTS_FONTS }}
        >
          <PaymentForm
            registerSubmit={registerSubmit}
            onError={onError}
            onFailed={onFailed}
            onSucceeded={onSucceeded}
            onSubmittingChange={onSubmittingChange}
            defaultName={defaultName}
          />
        </Elements>
      ) : (
        <PaymentFieldsSkeleton />
      )}
    </Box>
  )
}
